import React, { useEffect, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import MealSummaryCard from '../../components/MealSummaryCard';
import client from '../../api/client';
import { Wallet, Users, Calendar, TrendingUp, ArrowRight, Heart, Building2, Loader2, Globe, MapPin, Mail, Sparkles, CheckCircle2 } from 'lucide-react';

export default function Overview({ onNavigate }) {
  const [data, setData] = useState({ budget: null, guests: [], vendors: [], timeline: null, profile: null, website: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, g, v, t, p, w] = await Promise.all([
          client.get('/budget'),
          client.get('/guests'),
          client.get('/vendors'),
          client.get('/timeline'),
          client.get('/profile'),
          client.get('/website/config').catch(() => ({ data: null })),
        ]);
        setData({ budget: b.data, guests: g.data, vendors: v.data, timeline: t.data, profile: p.data, website: w.data });
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading || !data.budget) {
    return (
      <div className="flex items-center gap-2 text-[#6A6A6A]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your dashboard…
      </div>
    );
  }

  const { budget, guests, vendors, timeline, profile, website } = data;
  const spent = budget.categories.reduce((a, c) => a + (c.spent || 0), 0);
  const spentPercent = budget.total ? Math.round((spent / budget.total) * 100) : 0;
  const rsvpYes = guests.filter(g => g.rsvp === 'Yes').length;
  const rsvpPending = guests.filter(g => g.rsvp === 'Pending').length;
  // Headcount (people) — Yes invites + confirmed plus-ones. Matches the
  // Wedding dashboard so the couple never sees two different totals.
  const plusOneComing = (g) => (g.plusOneComing === null || g.plusOneComing === undefined ? !!g.plusOne : !!g.plusOneComing);
  const attendingHead = rsvpYes + guests.filter(g => g.rsvp === 'Yes' && plusOneComing(g)).length;
  const totalPeopleInvited = guests.length + guests.filter(g => plusOneComing(g)).length;
  const vendorsBooked = vendors.filter(v => v.status === 'Booked').length;
  const phasesAll = (timeline?.phases || []);
  const totalTasks = phasesAll.reduce((a, p) => a + p.tasks.length, 0);
  const doneTasks = phasesAll.reduce((a, p) => a + p.tasks.filter(t => t.done).length, 0);
  const currentPhase = phasesAll.find(p => p.tasks.some(t => !t.done)) || phasesAll[0];

  const today = new Date();
  const wd = profile?.weddingDate ? new Date(profile.weddingDate + 'T00:00:00') : null;
  const daysLeft = wd ? Math.max(0, Math.ceil((wd - today) / (1000 * 60 * 60 * 24))) : null;
  const greetName = profile?.partner1 || 'friend';

  // ----- Stage-aware "Jump right in" next-best actions -----
  const taskPercent = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const allActions = [
    { id: 'date',     when: !profile?.weddingDate,                 title: 'Set your wedding date',   sub: 'Unlocks the countdown and timeline.',     to: 'timeline',    icon: Calendar },
    { id: 'venue',    when: !profile?.venue && !profile?.location, title: 'Pick a venue or city',     sub: 'Tell us where — even just a region.',     to: 'vendors',     icon: MapPin },
    { id: 'guests',   when: guests.length < 5,                     title: 'Draft your guest list',    sub: 'Start with the must-invites.',            to: 'guests',      icon: Users },
    { id: 'budget',   when: spent === 0,                           title: 'Allocate your budget',     sub: 'Drag the sliders to fit your priorities.', to: 'budget',     icon: Wallet },
    { id: 'vendors',  when: vendors.length === 0,                  title: 'Add a few vendor leads',   sub: 'Even just names — we\u2019ll keep track.', to: 'vendors',    icon: Building2 },
    { id: 'std',      when: profile?.weddingDate && daysLeft > 90 && rsvpYes === 0, title: 'Send Save-the-Dates', sub: 'Lock in your guests\u2019 calendars early.', to: 'savethedate', icon: Mail },
    { id: 'website',  when: !website?.published,                   title: website?.slug ? 'Publish your wedding site' : 'Build your wedding website', sub: 'Share the day with everyone in one link.', to: 'website',     icon: Globe },
    { id: 'rsvps',    when: rsvpPending > 0 && totalTasks > 0,     title: 'Send RSVP links to guests', sub: `${rsvpPending} guests still pending.`,    to: 'guests',     icon: Mail },
    { id: 'tasks',    when: taskPercent < 30 && totalTasks > 0,    title: 'Knock out a few checklist items', sub: `${doneTasks}/${totalTasks} done — let\u2019s go.`, to: 'timeline', icon: CheckCircle2 },
  ];
  const nextActions = allActions.filter(a => a.when).slice(0, 3);

  return (
    <div className="animate-float-up">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <p className="section-eyebrow">Welcome back, {greetName}</p>
          <h1 className="font-serif text-4xl lg:text-5xl mt-2 text-[#2A2A2A]">
            {daysLeft != null ? <>{daysLeft} days <span className="italic text-[#7B2C3B]">until “I do.”</span></> : <span className="italic text-[#7B2C3B]">Set your date to start the countdown.</span>}
          </h1>
        </div>
        <button
          onClick={() => onNavigate('ai')}
          className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium self-start"
        >
          Ask your wedding friend
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard feature="budget" label="Budget spent" value={`$${spent.toLocaleString()}`} sub={`of $${budget.total.toLocaleString()}`} progress={Math.min(100, spentPercent)} onClick={() => onNavigate('budget')} />
        <StatCard feature="guests" label="Confirmed headcount" value={`${attendingHead}/${totalPeopleInvited}`} sub={`${rsvpYes}/${guests.length} invites said yes${rsvpPending ? ` · ${rsvpPending} pending` : ''}`} progress={totalPeopleInvited ? Math.round(attendingHead / totalPeopleInvited * 100) : 0} onClick={() => onNavigate('guests')} delay={80} />
        <StatCard feature="vendors" label="Vendors booked" value={`${vendorsBooked}/${vendors.length}`} sub="Keep going" progress={vendors.length ? Math.round(vendorsBooked / vendors.length * 100) : 0} onClick={() => onNavigate('vendors')} delay={160} />
        <StatCard feature="timeline" label="Tasks done" value={`${doneTasks}/${totalTasks}`} sub="On schedule" progress={totalTasks ? Math.round(doneTasks / totalTasks * 100) : 0} onClick={() => onNavigate('timeline')} delay={240} />
      </div>

      <div className="mb-8"><MealSummaryCard /></div>

      {(() => {
        const hasKey = profile?.weddingDate && (profile?.location || profile?.venue) && profile?.budgetTotal;
        if (hasKey) return null;
        return (
          <div data-testid="complete-setup-banner" className="mb-8 rounded-2xl p-5 border border-[#EFE3D8] bg-white flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="section-eyebrow">Finish your setup</p>
              <h3 className="font-serif text-xl text-[#2A2A2A] mt-1">A few quick answers unlock the rest of the dashboard</h3>
              <p className="text-sm text-[#6A6A6A] mt-1">
                We&apos;re missing your{' '}
                {[!profile?.weddingDate && 'wedding date', !(profile?.location || profile?.venue) && 'venue or city', !profile?.budgetTotal && 'budget'].filter(Boolean).join(', ')}. Add them now and the timeline, budget and site will feel personal instantly.
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                data-testid="complete-setup-redo-btn"
                onClick={() => onNavigate('settings')}
                className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
              >
                Open profile <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })()}

      {nextActions.length > 0 && (
        <div data-testid="jump-right-in" className="soft-card rounded-2xl p-6 mb-8 border border-[#EFE3D8]" style={{ background: 'linear-gradient(110deg, #F4E4DA 0%, #FBF7F2 60%)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="section-eyebrow">Jump right in</p>
              <h3 className="font-serif text-2xl text-[#2A2A2A] mt-1">Your next 3 best moves</h3>
            </div>
            <Sparkles className="w-5 h-5 text-[#7B2C3B]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {nextActions.map((a, idx) => {
              return (
                <button
                  key={a.id}
                  data-testid={`next-action-${a.id}`}
                  onClick={() => onNavigate(a.to)}
                  className="text-left p-4 rounded-2xl bg-white border border-[#EFE3D8] hover:border-[#7B2C3B] hover:-translate-y-0.5 transition-all group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <AppIcon feature={a.to} size="md" interactive reveal delay={320 + idx * 80} />
                    <span className="text-[10px] text-[#9A8A7E]">#{idx + 1}</span>
                  </div>
                  <p className="font-serif text-lg text-[#2A2A2A] leading-tight">{a.title}</p>
                  <p className="text-xs text-[#6A6A6A] mt-1">{a.sub}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs text-[#7B2C3B] group-hover:underline">
                    Open <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 soft-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-2xl text-[#2A2A2A]">This phase</h3>
            <span className="text-xs px-3 py-1 rounded-full tag-chip">{currentPhase?.date || ''}</span>
          </div>
          <ul className="space-y-3">
            {currentPhase?.tasks.slice(0, 6).map((t, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-md border-2 ${t.done ? 'bg-[#7B2C3B] border-[#7B2C3B]' : 'border-[#B8896C]'} flex items-center justify-center flex-shrink-0`} />
                <span className={`${t.done ? 'line-through text-[#9A8A7E]' : 'text-[#2A2A2A]'}`}>{t.text}</span>
              </li>
            ))}
          </ul>
          <button onClick={() => onNavigate('timeline')} className="mt-6 text-sm text-[#7B2C3B] hover:underline inline-flex items-center gap-1">
            View full timeline <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="soft-card rounded-2xl p-6 bg-[#F6EAE0]">
          <Heart className="w-6 h-6 text-[#7B2C3B] mb-3" />
          <h3 className="font-serif text-xl text-[#2A2A2A] mb-2">A nudge from your friend</h3>
          <p className="text-sm text-[#4A4A4A] leading-relaxed mb-4">
            {spent > budget.total ?
              <>You’re <span className="font-medium text-[#7B2C3B]">${(spent - budget.total).toLocaleString()} over</span>. Let’s rebalance — chat with your wedding friend for ideas.</> :
              <>You’ve spent <span className="font-medium text-[#7B2C3B]">{spentPercent}% of your budget</span>. {vendorsBooked < vendors.length ? 'Lock in remaining vendors before peak pricing.' : 'You’re tracking beautifully.'}</>
            }
          </p>
          <button onClick={() => onNavigate('ai')} className="btn-outline text-xs font-medium px-4 py-2 rounded-full">
            Ask the AI friend
          </button>
        </div>
      </div>

      {/* ShareMyPartyPics cross-promo */}
      <div className="mt-6 rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(120deg, #F4E7FA 0%, #FCEAF1 100%)', border: '1px solid #E8D2F0' }}>
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 p-6 lg:p-7">
          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#7B2C7B' }}>From the same makers</p>
            <h3 className="font-serif text-2xl lg:text-3xl text-[#2A2A2A] mt-2 leading-tight">
              Every guest. Every photo. <span className="italic" style={{ color: '#A639C8' }}>One shared album.</span>
            </h3>
            <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">
              Add ShareMyPartyPics to your wedding day — one QR code, every guest's photos in one private album. Ad-free, app-free, yours forever.
            </p>
            <a href="https://sharemypartypics.com" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-all" style={{ background: 'linear-gradient(90deg, #4A1FA9 0%, #C8377E 100%)' }}>
              Learn more →
            </a>
          </div>
          <div className="flex items-center justify-center">
            <img src="/images/smpp-logo.png" alt="Share My Party Pics" className="w-40 h-40 object-cover rounded-2xl shadow-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ feature, label, value, sub, progress, onClick, delay = 0 }) {
  return (
    <button data-testid={`stat-card-${feature}`} onClick={onClick} className="soft-card rounded-2xl p-5 text-left hover:translate-y-[-2px] transition-all group">
      <div className="flex items-center justify-between mb-3">
        <AppIcon feature={feature} size="md" interactive reveal delay={delay} />
        <TrendingUp className="w-4 h-4 text-[#B8896C] opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <p className="text-xs text-[#6A6A6A]">{label}</p>
      <p className="font-serif text-2xl text-[#2A2A2A] mt-1">{value}</p>
      <p className="text-xs text-[#6A6A6A] mt-0.5">{sub}</p>
      <div className="mt-3 h-1 w-full rounded-full bg-[#F4E4DA] overflow-hidden">
        <div className="h-full rounded-full bg-[#7B2C3B] transition-all" style={{ width: `${progress}%` }} />
      </div>
    </button>
  );
}
